/**
 * Küchengeräte in der Seitenansicht: Deckel, Sieb, Gießgefäß, Salzstreuer
 * und ein Flüssigkeitsstrahl.
 *
 * Wo nicht anders angegeben, steht ein Gerät mit der Unterkante auf y und
 * ist um cx zentriert.
 */
import { fract } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path, roundedRectPath, smooth } from '../paths.ts';
import { circle, ellipse, group, line, rect, rotated, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';

/** Topfdeckel; rimY ist die Höhe des Deckelrands. */
export function lid(cx: number, rimY: number, width: number): SceneNode[] {
  const rx = width / 2;
  const ry = Math.max(5, width * 0.14);
  const dome = path()
    .move(cx - rx, rimY)
    .curve(cx - rx * 0.8, rimY - ry * 2.1, cx + rx * 0.8, rimY - ry * 2.1, cx + rx, rimY)
    .arc(rx, ry * 0.5, false, true, cx - rx, rimY)
    .close()
    .toString();
  const knobY = rimY - ry * 1.55;
  return [
    shape(dome, styles.solid('shade')),
    shape(
      path()
        .move(cx - rx * 0.62, rimY - ry * 0.7)
        .quad(cx - rx * 0.4, rimY - ry * 1.2, cx - rx * 0.1, rimY - ry * 1.35)
        .toString(),
      {
        stroke: 'tint',
        strokeWidth: STROKE.outline,
      },
    ),
    shape(roundedRectPath(cx - 9, knobY - 6, 18, 7, 3), styles.solid('ink')),
  ];
}

/**
 * Nudelsieb mit Löchern, zwei Griffen und Standring.
 * @param rimY Höhe des Rands; die Unterkante liegt bei rimY + height.
 */
export function colander(
  cx: number,
  rimY: number,
  width: number,
  height: number,
): { back: SceneNode[]; front: SceneNode[]; opening: string } {
  const rx = width / 2;
  const ry = Math.max(5, width * 0.13);
  const bottom = rimY + height - 5;
  const body = path()
    .move(cx - rx, rimY)
    .arc(rx, ry, false, false, cx + rx, rimY)
    .curve(cx + rx, rimY + height * 0.55, cx + rx * 0.62, bottom, cx + rx * 0.35, bottom)
    .line(cx - rx * 0.35, bottom)
    .curve(cx - rx * 0.62, bottom, cx - rx, rimY + height * 0.55, cx - rx, rimY)
    .close()
    .toString();
  const opening = ellipsePath(cx, rimY, rx, ry);
  const holes: SceneNode[] = [];
  const rows = [
    { y: rimY + ry + 7, span: 0.7, count: 6 },
    { y: rimY + ry + 15, span: 0.56, count: 5 },
    { y: rimY + ry + 23, span: 0.42, count: 4 },
  ];
  for (const row of rows) {
    if (row.y > bottom - 3) {
      continue;
    }
    for (let index = 0; index < row.count; index += 1) {
      const x = cx - rx * row.span + ((rx * row.span * 2) / (row.count - 1)) * index;
      holes.push(circle(x, row.y, 1.8, styles.flat('cavity')));
    }
  }
  const handle = (side: -1 | 1): SceneNode =>
    shape(roundedRectPath(side < 0 ? cx - rx - 11 : cx + rx - 3, rimY + 2, 14, 6, 3), styles.solid('ink'));
  return {
    back: [
      handle(-1),
      handle(1),
      shape(opening, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.outline }),
    ],
    front: [
      rect(cx - rx * 0.38, bottom - 1, rx * 0.76, 6, 2, styles.solid('paper')),
      shape(body, styles.solid('paper')),
      ...holes,
    ],
    opening,
  };
}

/**
 * Flacher Strahl (Wasser, Wein, Teig) entlang von Punkten. Die Fläche ist
 * deckend; kurze helle Striche wandern mit und zeigen das Fließen.
 */
export function stream(points: readonly Point[], width: number, t: number, fill: Tone = 'tint'): SceneNode {
  if (points.length < 2 || width <= 0.2) {
    return group([]);
  }
  // Mittellinie fein abtasten, dann beidseitig versetzen.
  const centre: Point[] = [];
  const steps = 16;
  for (let index = 0; index <= steps; index += 1) {
    const f = index / steps;
    const scaled = f * (points.length - 1);
    const at = Math.min(points.length - 2, Math.floor(scaled));
    const local = scaled - at;
    const a = points[at] as Point;
    const b = points[at + 1] as Point;
    centre.push({ x: a.x + (b.x - a.x) * local, y: a.y + (b.y - a.y) * local });
  }
  const left: Point[] = [];
  const right: Point[] = [];
  centre.forEach((point, index) => {
    const before = centre[Math.max(0, index - 1)] as Point;
    const after = centre[Math.min(centre.length - 1, index + 1)] as Point;
    const angle = Math.atan2(after.y - before.y, after.x - before.x);
    // Der Strahl wird nach unten etwas schmaler.
    const half = (width / 2) * (1 - 0.25 * (index / steps));
    left.push({ x: point.x - Math.sin(angle) * half, y: point.y + Math.cos(angle) * half });
    right.push({ x: point.x + Math.sin(angle) * half, y: point.y - Math.cos(angle) * half });
  });
  const outline = `${smooth([...left, ...right.reverse()], true, 0.6)}`;
  const marks: MaybeNode[] = [0, 0.33, 0.66].map((offset) => {
    const f = fract(t * 3 + offset);
    const index = Math.min(centre.length - 2, Math.floor(f * (centre.length - 1)));
    const a = centre[index] as Point;
    const b = centre[index + 1] as Point;
    return line(a.x, a.y, b.x, b.y, { stroke: 'paper', strokeWidth: STROKE.detail });
  });
  return group([
    shape(outline, { fill, stroke: 'ink', strokeWidth: STROKE.detail }),
    group(marks, { clip: outline }),
  ]);
}

/**
 * Gießgefäß (Becher mit Henkel) mit Inhalt, gekippt um die Ausgussecke.
 * Die Flüssigkeit bleibt waagerecht, auch wenn der Becher kippt.
 * @param lip Ausgussecke oben links im Bild.
 * @param tilt Neigung in Grad; negativ kippt nach links.
 * @param fullness Füllstand 0 bis 1.
 */
export function pourer(
  lip: Point,
  width: number,
  height: number,
  tilt: number,
  fullness: number,
  liquid: Tone,
): SceneNode {
  const x0 = lip.x;
  const y0 = lip.y;
  const body = path()
    .move(x0, y0)
    .line(x0 + width, y0)
    .line(x0 + width - 4, y0 + height - 4)
    .quad(x0 + width - 4, y0 + height, x0 + width - 8, y0 + height)
    .line(x0 + 8, y0 + height)
    .quad(x0 + 4, y0 + height, x0 + 4, y0 + height - 4)
    .close()
    .toString();
  // Wasserspiegel: waagerecht im Bild, also im Becher um -tilt gedreht.
  const surfaceLocal = { x: x0 + width / 2, y: y0 + height * (1 - fullness) };
  const liquidNode =
    fullness > 0.01
      ? group(
          [
            rotated(-tilt, surfaceLocal.x, surfaceLocal.y, [
              rect(surfaceLocal.x - 80, surfaceLocal.y, 160, 120, 0, styles.flat(liquid)),
            ]),
          ],
          {
            clip: body,
          },
        )
      : null;
  return rotated(tilt, x0, y0, [
    shape(
      path()
        .move(x0 + width - 1, y0 + 8)
        .curve(x0 + width + 14, y0 + 8, x0 + width + 14, y0 + height - 10, x0 + width - 3, y0 + height - 10)
        .toString(),
      { stroke: 'ink', strokeWidth: STROKE.outline },
    ),
    shape(body, styles.flat('paper')),
    liquidNode,
    shape(body, styles.outline()),
    line(x0 + 3, y0, x0 + width - 3, y0, styles.detail()),
  ]);
}

/** Salzstreuer, Mitte (x, y), um tilt Grad gekippt. */
export function saltShaker(x: number, y: number, tilt = 150): SceneNode {
  return rotated(tilt, x, y, [
    shape(roundedRectPath(x - 8, y - 6, 16, 20, 4), styles.solid('paper')),
    shape(
      path()
        .move(x - 8, y - 4)
        .quad(x, y - 16, x + 8, y - 4)
        .close()
        .toString(),
      styles.solid('shade'),
    ),
    circle(x - 3, y - 8, 1, styles.flat('ink')),
    circle(x + 3, y - 8, 1, styles.flat('ink')),
    circle(x, y - 11, 1, styles.flat('ink')),
  ]);
}

/** Flacher Teller von der Seite, Oberkante auf y. */
export function plate(cx: number, y: number, width: number): SceneNode[] {
  const rx = width / 2;
  return [
    shape(
      path()
        .move(cx - rx, y)
        .quad(cx - rx * 0.7, y + 9, cx - rx * 0.4, y + 9)
        .line(cx + rx * 0.4, y + 9)
        .quad(cx + rx * 0.7, y + 9, cx + rx, y)
        .close()
        .toString(),
      styles.solid('paper'),
    ),
    ellipse(cx, y, rx, 5, styles.solid('paper')),
    ellipse(cx, y, rx * 0.66, 3.2, { fill: 'none', stroke: 'shade', strokeWidth: STROKE.fine }),
  ];
}

/**
 * Feines Mehlsieb von der Seite: flache Siebschale mit Gitter, Rand und
 * Stiel nach links. Mitte des Rands in (cx, rimY).
 */
export function flourSieve(cx: number, rimY: number, width: number): SceneNode[] {
  const rx = width / 2;
  const depth = width * 0.32;
  const basket = path()
    .move(cx - rx, rimY)
    .curve(cx - rx, rimY + depth * 1.2, cx + rx, rimY + depth * 1.2, cx + rx, rimY)
    .close()
    .toString();
  const mesh: SceneNode[] = [];
  for (let index = -5; index <= 5; index += 1) {
    const x = cx + index * (rx / 5.5);
    mesh.push(line(x - 6, rimY, x + 6, rimY + depth, styles.fine('deep')));
    mesh.push(line(x + 6, rimY, x - 6, rimY + depth, styles.fine('deep')));
  }
  return [
    shape(roundedRectPath(cx - rx - 40, rimY - 4, 42, 7, 3.5), styles.solid('ink')),
    shape(basket, styles.flat('paper')),
    group(mesh, { clip: basket }),
    shape(basket, styles.outline()),
    shape(roundedRectPath(cx - rx - 3, rimY - 4, width + 6, 6, 3), styles.solid('shade')),
  ];
}

/** Kuchengitter von der Seite mit Füßen; Auflage bei topY. */
export function coolingRack(cx: number, topY: number, width: number, groundY: number): SceneNode[] {
  const rx = width / 2;
  const wires: SceneNode[] = [];
  for (let index = 0; index <= 12; index += 1) {
    const x = cx - rx + (width / 12) * index;
    wires.push(line(x, topY, x, topY + 3, styles.fine('ink')));
  }
  return [
    line(cx - rx + 8, topY, cx - rx + 4, groundY, styles.outline()),
    line(cx + rx - 8, topY, cx + rx - 4, groundY, styles.outline()),
    rect(cx - rx, topY, width, 3, 1.5, styles.solid('paper')),
    ...wires,
  ];
}
